import ReactDropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Badge } from '@monksflow/monks-ui';
import { twMerge } from 'tailwind-merge';
import { DropzoneProps } from './Dropzone.types';

const sizeClasses = {
  small: 'py-2 px-4',
  medium: 'py-4 px-8',
  large: 'py-13 px-8',
};

// `twMerge` so a caller's surface class replaces the resting one instead of
// racing it in the stylesheet; the drag-active surface still wins because it is
// applied after, in the same argument list.
const dropzoneClasses = (isDragActive: boolean, size: DropzoneProps['size'], className?: string) =>
  twMerge(
    'flex w-full cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed py-2 transition-colors duration-fast ease-out',
    sizeClasses[size ?? 'large'],
    'border-border bg-muted',
    className,
    isDragActive && 'border-primary bg-accent',
  );

export const Dropzone = ({
  onDrop,
  onDropRejected,
  acceptedFormats = {},
  title,
  description,
  size = 'large',
  showcasedFormats = [],
  className,
  icon,
}: DropzoneProps) => {
  const { t } = useTranslation('common');
  const titleText = title ?? t('components.dropzone.title');
  const descriptionText = description ?? t('components.dropzone.description');

  return (
    <ReactDropzone
      onDrop={onDrop}
      onDropRejected={onDropRejected}
      multiple
      accept={{
        ...acceptedFormats,
      }}
      disabled={false}
    >
      {({ getRootProps, getInputProps, isDragActive }) => (
        <div
          {...getRootProps()}
          data-testid="dropzone"
          className={dropzoneClasses(isDragActive, size, className)}
        >
          <input {...getInputProps()} />
          <div className="flex w-full flex-col items-center gap-4">
            <div className="flex flex-row items-center gap-2">
              {icon}
              <span className="text-body-md text-foreground">{titleText}</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="flex flex-row flex-wrap justify-center gap-2">
                {showcasedFormats.map((format) => (
                  <Badge variant="outline" key={format}>
                    {format}
                  </Badge>
                ))}
              </div>
              <span className="text-body-sm text-muted-foreground">{descriptionText}</span>
            </div>
          </div>
        </div>
      )}
    </ReactDropzone>
  );
};

Dropzone.displayName = 'Dropzone';
